import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import TopNavigation from '../components/TopNavigation';
import { getUserMe } from '../utils/secureFunctions';

type StudioLink = { to: string; title: string; description: string };

const CREATOR_LINKS: StudioLink[] = [
  { to: '/earnings', title: 'Earnings', description: 'Track creator income, gifts and withdrawals.' },
  { to: '/advertiser-dashboard', title: 'Ads & wallet', description: 'Fund your wallet with PayPal and run promotions.' },
  { to: '/go-live', title: 'Go live', description: 'Start a live stream and chat with your community.' },
  { to: '/pulse', title: 'Pulse', description: 'Create short videos across every category.' },
  { to: '/testimony', title: 'Testimonies', description: 'Share and manage your testimonies.' },
  { to: '/music-library', title: 'Music library', description: 'Manage tracks for your videos and reels.' },
  { to: '/wallet-transactions', title: 'Wallet history', description: 'Review top-ups, ad spend and payouts.' },
];

const GUIDE_LINKS: StudioLink[] = [
  { to: '/docs', title: 'Guides & docs', description: 'Learn how every Belivah feature works.' },
  { to: '/playbook', title: 'Creator playbook', description: 'Practical steps to grow your ministry.' },
  { to: '/help', title: 'Help center', description: 'FAQs and support contacts.' },
];

const STAFF_ROLES = new Set(['admin', 'super_admin', 'moderator']);

const CreatorStudio: React.FC = () => {
  const [isStaff, setIsStaff] = useState(false);

  useEffect(() => {
    let active = true;
    getUserMe()
      .then((me) => {
        const role = (me.profile as { role?: string } | undefined)?.role;
        if (active) setIsStaff(!!role && STAFF_ROLES.has(role));
      })
      .catch(() => undefined);
    return () => { active = false; };
  }, []);

  const renderGrid = (links: StudioLink[]) => (
    <div className="studio-grid" style={styles.grid}>
      {links.map((l) => (
        <Link key={l.to} to={l.to} style={styles.card}>
          <span style={styles.cardTitle}>{l.title}</span>
          <span style={styles.cardDesc}>{l.description}</span>
        </Link>
      ))}
    </div>
  );

  return (
    <div style={styles.container}>
      <TopNavigation />
      <main style={styles.content}>
        <header style={styles.header}>
          <h1 style={styles.title}>Creator Studio</h1>
          <p style={styles.subtitle}>Your advanced dashboards for content, audience, earnings and promotions.</p>
        </header>
        <section style={styles.section}>
          <h2 style={styles.h2}>Dashboards</h2>
          {renderGrid(isStaff ? [
            ...CREATOR_LINKS,
            { to: '/studio', title: 'Admin Dashboard', description: 'Moderation, users, reports and platform settings.' },
            { to: '/analytics', title: 'Platform analytics', description: 'Reach, engagement and growth across Belivah.' },
            { to: '/appeals', title: 'Moderation appeals', description: 'Review flagged and disputed media.' },
          ] : CREATOR_LINKS)}
        </section>
        <section style={styles.section}>
          <h2 style={styles.h2}>Learn</h2>
          {renderGrid(GUIDE_LINKS)}
        </section>
      </main>
    </div>
  );
};

const styles: { [k: string]: React.CSSProperties } = {
  container: { minHeight: '100vh', background: 'var(--color-background-alt)' },
  content: { maxWidth: 960, margin: '0 auto', padding: '84px 16px 24px' },
  header: { background: '#0B6B6F', color: '#fff', padding: '20px 24px', borderRadius: 12 },
  title: { margin: 0, fontSize: 28, color: '#fff' },
  subtitle: { margin: '6px 0 0 0', fontSize: 14, color: 'rgba(255,255,255,0.9)' },
  section: { marginTop: 20 },
  h2: { margin: '0 0 12px 0', color: 'var(--color-text-primary)', fontSize: 20 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 },
  card: {
    display: 'flex', flexDirection: 'column', gap: 6, padding: 16, borderRadius: 12, textDecoration: 'none',
    background: 'var(--color-surface)', border: '1px solid var(--color-border)', boxShadow: 'var(--shadow-sm)',
  },
  cardTitle: { fontWeight: 700, fontSize: 16, color: 'var(--color-primary-700, #0B6B6F)' },
  cardDesc: { fontSize: 13, color: 'var(--color-text-secondary)' },
};

export default CreatorStudio;
